import type { AthleteSettings, Dow, Preparation } from '@/api/engineService';

export const DAYS: { value: Dow; label: string; short: string }[] = [
  { value: 1, label: 'Lundi', short: 'L' },
  { value: 2, label: 'Mardi', short: 'M' },
  { value: 3, label: 'Mercredi', short: 'M' },
  { value: 4, label: 'Jeudi', short: 'J' },
  { value: 5, label: 'Vendredi', short: 'V' },
  { value: 6, label: 'Samedi', short: 'S' },
  { value: 7, label: 'Dimanche', short: 'D' },
];

export const SPORT_SHORT: Record<string, string> = { RUNNING: 'CAP', CYCLING: 'Vélo', SWIMMING: 'Nat', PPG: 'PPG' };

export const PREPARATION_LABELS: Record<Preparation, string> = { IDEAL: 'Idéale', REDUCED: 'Réduite', CONDENSED: 'Condensée' };
export const PREPARATION_COLORS: Record<Preparation, 'green' | 'yellow' | 'red'> = { IDEAL: 'green', REDUCED: 'yellow', CONDENSED: 'red' };

export const todayIso = (): string => {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
};

export const addDaysIso = (date: string, days: number): string => {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d) + days * 86_400_000);
  return t.toISOString().slice(0, 10);
};

export const mondayOf = (date: string): string => {
  const [y, m, d] = date.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay() || 7;
  return addDaysIso(date, 1 - dow);
};

export const formatFr = (iso: string | undefined | null): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export const formatHours = (sec: number): string => {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m} min`;
};

export type DateState = 'disabled' | 'condensed' | 'reduced' | 'ideal';

export const dateStateOf = (
  competitionDate: string,
  bounds: { condensedFromWeek: string | null; reducedFromWeek: string; idealFromWeek: string; latestWeek: string }
): DateState => {
  const C = mondayOf(competitionDate);
  if (C > bounds.latestWeek) return 'disabled';
  if (C >= bounds.idealFromWeek) return 'ideal';
  if (C >= bounds.reducedFromWeek) return 'reduced';
  if (bounds.condensedFromWeek !== null && C >= bounds.condensedFromWeek) return 'condensed';
  return 'disabled';
};

export const DATE_STATE_LABELS: Record<DateState, string> = {
  disabled: 'Date refusée',
  condensed: 'Préparation condensée',
  reduced: 'Préparation réduite',
  ideal: 'Préparation idéale',
};

export const DATE_STATE_CLASSES: Record<DateState, string> = {
  disabled: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  condensed: 'bg-orange-100 text-orange-700',
  reduced: 'bg-amber-100 text-amber-700',
  ideal: 'bg-success-500/15 text-success-600',
};

/** The engine settings behind the simulator filter state. */
export const settingsOf = (state: {
  sessionsPerWeek: Record<string, number>;
  days: string[];
  brickDow: Dow | null;
  includePpg: boolean;
}): AthleteSettings => ({
  sessionsPerWeek: state.sessionsPerWeek,
  sportDays: state.days.map((k) => ({ dow: Number(k.split(':')[0]) as Dow, sportId: k.split(':')[1] })),
  brickDow: state.brickDow,
  includePpg: state.includePpg,
});
